import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core"
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { NODE_TYPE } from "../../common/linqset-model"
import { ACTION_TYPE, ActionType, DEFAULT_CURRENCY, ModelInsights, getActionTypeName } from "../../common/organisation";
import { getActionTypeColour, MODEL_COLOURS } from "../../model-services/model-item-enums";
import { BarData } from "../../charts/bar-chart/bar-chart.component";
import { LinqChartData } from "../../charts/linq-chart-data";
import { OrganisationService } from "../../organisation/organisation.service";

const PIE_WIDTH = 250

class RowData {
    values:any = {}

    public number:number
    public hours:number
    public cost:number
    public percentage:number
    public hoursPercentage:number
    public costPercentage:number

    constructor(public rowType:ACTION_TYPE, public icons:NODE_TYPE[], public name:string, private data:ActionType, private comparison?:ActionType) {
    }

    getValue(column:string) {
        if (this.comparison) {
            if (column.endsWith('2')) {
                return this.data[column.substring(0, column.length-1)]
            }else return this.comparison[column]
        }else {
            return this.data[column]
        }
    }

    hasComparison() {return this.comparison?true:false}

}

@Component({
    selector: 'app-action-types',
    templateUrl: './action-types.html',
    styleUrls: ['./action-types.scss'],
    standalone: false
})
export class ActionTypesComponent implements OnInit, OnChanges {
    @Input() insights:ModelInsights
    @Input() comparison:ModelInsights

    @Output() selectRowEvent = new EventEmitter<ACTION_TYPE>()
    
    columns = []
    headings:any

    dataSource = new MatTableDataSource<RowData>();
    @ViewChild(MatSort) sort: MatSort

    percentageAutomated = 0
    percentageProcessedAutomated = 0

    pieData:LinqChartData[]
    barData:{name:string, data:BarData[]}[]
    stacks:string[]

    selectedType:ACTION_TYPE
    totals = []

    currencyCode:string

    constructor(private organisationService:OrganisationService) {}

    ngOnInit(): void {
        this.currencyCode = this.organisationService.organisation?.currencyCode||DEFAULT_CURRENCY
        this.populateDataSource()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['insights'] || changes["comparison"]) this.populateDataSource()
    }

    private getNodeTypes(type:ACTION_TYPE):NODE_TYPE[] {
        switch(type) {
            case ACTION_TYPE.NONE:
                return [NODE_TYPE.ACTION]
            case ACTION_TYPE.AUTOMATED:
                return [NODE_TYPE.SYSTEM]
            case ACTION_TYPE.MANUAL:
                return [NODE_TYPE.ROLE]
            case ACTION_TYPE.BOTH:
                return [NODE_TYPE.ROLE, NODE_TYPE.SYSTEM]
            case ACTION_TYPE.EXTERNAL:
                return [NODE_TYPE.EXTERNAL_ORGANISATION]
        }
    }

    setupHeadings() {
        this.headings = {}
        this.headings['icons'] = 'Types'
        this.headings['name'] = 'Name'
        this.headings['colour'] = 'Legend'
        this.headings['number'] = 'Number'
        this.headings['processed'] = 'Processed'
        this.headings['percentage'] = '%'
        this.headings['hours'] = 'Hours'
        this.headings['hoursPercentage'] = '%'
        this.headings['cost'] = 'Cost'
        this.headings['costPercentage'] = '%'
    }

    populateDataSource() {
        this.setupHeadings()
        this.columns = ['icons', 'name', 'colour']
        
        if (this.comparison) {
            this.columns = this.columns.concat(['number', 'number2', 'processed', 'processed2', 'hours', 'hours2', 'cost', 'cost2']);
        }else {
            this.columns = this.columns.concat(['number', 'processed', 'hours', 'cost']);
        }

        this.pieData = []
        const data:RowData[] = []
        this.stacks = []
        this.totals = this.columns.map(c => 0)

        if (this.insights.actionTypes) {
            Object.keys(this.insights.actionTypes).forEach(a => {
                const t = parseInt(a) as ACTION_TYPE
                const name = getActionTypeName(t)
                this.stacks.push(name)

                const row = new RowData(t, this.getNodeTypes(t), name, this.insights.actionTypes[t], this.comparison?.actionTypes[t])
                data.push(row)
                this.columns.forEach((c, j) => {
                    if (c !== 'icons' && c !== 'name' && c !== 'colour')
                        this.totals[j] += row.getValue(c)
                })

                this.pieData.push(new LinqChartData(name, this.insights.actionTypes[t].number, this.getColour(t)))
            })
        }

        this.dataSource.data = data
        this.dataSource.sort = this.sort
        this.barData = []

        this.columns.filter(column => (this.headings[column] && column !== 'icons' && column !== 'name' && column !== 'colour')).forEach(column => {
            const name = this.headings[column].length < 9?String(this.headings[column]).padEnd(9, ' '):this.headings[column]

            const bd = new BarData(column, name, [], [], [], [], [], [])
            const bdForColumn = {name:this.headings[column], data:[bd]}
            this.barData.push(bdForColumn)
            data.forEach(row => {
                const colour = this.getColour(row.rowType)
                bd.values.push(row.getValue(column))
                bd.colors.push(colour)
                bd.hoverColors.push(MODEL_COLOURS.EXCLUDED_COLOR)
                if (this.comparison) {
                    bd.csValues.push(row.getValue(column + "2"))
                    bd.csColors.push(colour)
                    bd.csHoverColors.push(MODEL_COLOURS.EXCLUDED_COLOR)
                }
            })
        })   
    }

    getHeaderStyle(column:string) {
        if (column.startsWith("number") || column.startsWith('hours'))
            return "background:rgba(0, 169, 165, 0.05);"

        return ""
    }

    getNumberStyle(row:any, column:string) {
        const background = this.getHeaderStyle(column)

        if (column.endsWith('2')) {
            const before = row.getValue(column.substring(0, column.length-1))
            const after = row.getValue(column)

            if (after > before) return "color:#15803d;font-weight:600;" + background
            else if (before > after) return "color:#b42318;font-weight:600;" + background
            else return "color:#9aa0b0;" + background
        }
        return background
    }

    getPieChartWidth() {
        return PIE_WIDTH
    }

    getPercentage(row:RowData, column:string, i:number) {
        return this.totals[i]?`(${Math.round(100*row.getValue(column)/this.totals[i])} %)`:''
    }

    hasHeading(column:string) {
        return this.headings[column]?true:false
    }

    getHeadings() {
        return this.columns?this.columns.filter(c => this.hasHeading(c)):[]
    }

    getHeadingWidth(column:string) {
        return this.comparison && column !== 'icons' && column !== 'name' && column !== 'colour'?2:1
    }

    getColour(type:ACTION_TYPE) {
        return getActionTypeColour(type)
    }


    selectRow(row:RowData) {
        this.selectedType = row.rowType
        this.selectRowEvent.emit(row.rowType)
    }

}
